import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Rankings, type DatosDeRanking } from "./Rankings.js";
import { AYUDAS, Ayuda, BarrasH, Caja, Serie, SerieDoble, Tramos } from "./graficos.js";
import { BusquedasAFondo, CompetenciaDelCliente, PaginasDelCliente } from "./SeoAFondo.js";

/**
 * El panel de SEO de un cliente, con la forma que pidió Jorge.
 *
 * Arriba las cifras que dicen si va bien; debajo, a la izquierda la curva y
 * las acciones, en medio las keywords y el informe, a la derecha la auditoría
 * y las alertas. Es el orden en que se mira, no el orden en que están
 * guardados los datos.
 *
 * **Todo viene de una sola llamada y nada se pide a Google al abrir.** Un
 * panel que consultara Google tardaría diez segundos, gastaría cuota cada vez
 * que alguien pasa por delante, y enseñaría números distintos de los del
 * informe que se le mandó al cliente.
 *
 * **Un hueco no es un cero.** Cuando falta un dato porque una fuente no está
 * enlazada, se dice cuál falta en vez de pintar un cero: confundir «no
 * conectado» con «no tiene» hace perder una tarde buscando un problema que no
 * existe.
 */

/**
 * Lo que la pantalla acepta para pintar una cifra con su alcance.
 *
 * Es el tipo que SÓLO produce `paraVista` en el servidor: aquí llega ya
 * serializado, así que el sello no viaja — pero la ruta no puede componer esta
 * lista de ninguna otra forma, y ahí es donde estaba el riesgo.
 */
interface CifraConAlcance {
  clave: string;
  etiqueta: string;
  valor: number | null;
  /**
   * Por qué no hay número, si no lo hay. Los seis, y ninguno vale por otro.
   *
   * `averia` y `enCola` se parecen y no son lo mismo: en la avería alguien
   * tiene que arreglar algo, en la cola el dato llega solo. Por eso sólo la
   * primera se pinta en rojo.
   */
  estado:
    "dato" | "sinCliente" | "sinConectar" | "noSeMedia" | "averia" | "enCola";
  /** Qué abarca. Se pinta SIEMPRE: un número sin su alcance afirma otra cosa. */
  alcance: string;
  /** Cómo se dice el hueco, ya en el idioma de la vista que toca. */
  comoSeDice: string | null;
  detalleInterno?: string;
  /** De qué está hecho el número, cuando está hecho de cosas distintas. */
  partes?: { que: string; cuantas: number }[];
  /** Algo que hay que saber para leer el número. Viaja CON él. */
  aviso?: string | null;
  /** Cuánto cambió respecto al periodo anterior. `null` = no hay con qué. */
  cambio: number | null;
  /**
   * Si eso es a mejor o a peor, ya decidido en el servidor.
   *
   * La pantalla NO lo calcula. Antes cada tarjeta recibía un `menosEsMejor` a
   * mano y en «Posición media» bajar es mejorar: el flag que se olvida pinta
   * una flecha verde en una caída.
   */
  sentido: "mejor" | "peor" | "igual" | "sinDato" | "desdeCero";
}

interface Datos {
  cliente: string;
  /** Qué mes se está viendo y cuáles se pueden pedir. Lo decide el servidor. */
  calendario: {
    mes: string;
    esElActual: boolean;
    /** Por qué se está viendo este mes y no otro. */
    porQue:
      | "elegido"
      | "ultimo-informe"
      | "ultimo-dato"
      | "mes-en-curso"
      | "sin-datos";
    /** Cada mes con lo que trae dentro, para no ofrecer pantallas en blanco. */
    disponibles: {
      mes: string;
      tiene: {
        informe: boolean;
        analytics: boolean;
        posiciones: boolean;
        auditoria: boolean;
      };
    }[];
  };
  /**
   * Lo que queda de la caja vieja de cifras: de qué mes es el informe.
   *
   * Aquí estaban declaradas las once que pintaba la fila de KPI a pelo. La
   * ruta las sigue mandando, pero la pantalla ya no las mira: todas entran por
   * `cifrasConAlcance`. Se quitan del tipo a propósito — mientras estuvieran
   * declaradas, volver a pintar una sin su alcance seguía siendo un
   * autocompletado de distancia.
   */
  cifras: {
    delMes: string | null;
  };
  curva: { keyword: string; puntos: { dia: string; posicion: number }[] }[];
  keywords: {
    texto: string;
    tipo: "servicio" | "informativa";
    principal: boolean;
    pagina: string | null;
    posicionInicial: number | null;
    hoy: number | null;
    haceUnaSemana: number | null;
    enIa: { mencionado: boolean; puesto: number | null; de: number } | null;
  }[];
  auditoria: {
    mes: string | null;
    /** Un percentil, NO una nota sobre cien. El nombre lo dice a propósito. */
    mejorQueElPorcentaje: number | null;
    errores: number | null;
    advertencias: number | null;
    avisos: number | null;
    problemas: number;
    paginasAfectadas: number;
    arreglados: number;
    nuevos: number;
    principales: {
      problema: string;
      /** error · advertencia · aviso. `null` = no consta, que NO es «leve». */
      gravedad: string | null;
      categoria: string;
      paginas: number;
      nuevos: number;
      solucionados: number;
      /** Unas cuantas paginas donde pasa, para poder ir a mirarlas. */
      ejemplos: string[];
      como: string | null;
      cuidado: string | null;
      plazo: string | null;
    }[];
  };
  autoridad: {
    mes: string;
    backlinks: number | null;
    dominiosDeReferencia: number | null;
    domainTrust: number | null;
    noIndexables: number | null;
  } | null;
  /** Lo rápida que va, de la última medición del barrido. */
  velocidad: {
    url: string;
    dia: string;
    nota: number | null;
    lcp: number | null;
    cls: number | null;
    tbt: number | null;
    deUsuariosReales: boolean;
    pesoTotalKb: number | null;
    recomendaciones: {
      id: string;
      que: string;
      como: string;
      plazo: string;
      ahorroMs: number;
    }[];
    antes: { dia: string; nota: number | null } | null;
  } | null;
  /** Las cifras de cabecera, con su alcance y su hueco explicado. */
  cifrasConAlcance: CifraConAlcance[];
  /** Las de Técnico, por la misma puerta. */
  tecnicoConAlcance: CifraConAlcance[];
  /** Y las de Tareas. Con ésta son las tres filas. */
  tareasConAlcance: CifraConAlcance[];
  /** Las de la ficha de Google. */
  localConAlcance: CifraConAlcance[];
  /** Todas las keywords de la última captura, con volumen, URL y movimiento. */
  rankings: DatosDeRanking;
  trafico: {
    mes: string | null;
    serie: {
      mes: string;
      sesiones: number | null;
      organicas: number | null;
      usuarios: number | null;
      usuariosOrganicos: number | null;
      paginasVistas: number | null;
      aMedias: boolean;
    }[];
    canales: { canal: string; sesiones: number }[];
  };
  ia: {
    mes: string | null;
    motores: string[];
    consultas: {
      keyword: string;
      porMotor: {
        modelo: string;
        medido: boolean;
        mencionado: boolean;
        posicion: number | null;
        de: number | null;
      }[];
    }[];
    sales: number;
    medidas: number;
    series: { mes: string; porcentaje: number | null }[];
  };
  conversiones: {
    mes: string | null;
    serie: { mes: string; total: number; aMedias: boolean }[];
    porTipo: { que: string; cuantas: number }[];
    /** Eventos que venían entrando y pararon de golpe. */
    seRompieron: { evento: string; solia: number; meses: number }[];
    tasa: number | null;
    tasaAntes: number | null;
  };
  /**
   * Series y repartos: lo que hace falta para pintar, no para decidir.
   *
   * Sale de `posiciones_seo`, que es la tabla con datos de verdad. El snapshot
   * mensual tiene columnas para casi todo esto y está casi vacío, así que no se
   * usa de fuente: pintar de una tabla vacía son ceros con cara de medidos.
   */
  paraPintar: {
    serie: { mes: string; posicionMedia: number; keywords: number }[];
    tramos: {
      mes: string;
      top3: number;
      cuatroADiez: number;
      onceATreinta: number;
      masAlla: number;
      sinPosicion: number;
    }[];
    empujones: {
      empujones: {
        consulta: string;
        posicion: number;
        impresiones: number;
        clics: number;
        objetivo: number;
        probabilidad: number;
        siLoConsigue: number;
        valor: number;
      }[];
      /** Cuántas se miraron. Sin esto, una lista vacía afirma de más. */
      miradas: number;
      descartadas: number;
    };
    problemasPorCategoria: {
      categoria: string;
      paginas: number;
      cuantos: number;
    }[];
    enlacesPorMes: {
      mes: string;
      backlinks: number | null;
      dominios: number | null;
      confianza: number | null;
    }[];
  };
  /** Las búsquedas que de verdad traen clics. No las que se persiguen. */
  busquedasQueTraen: {
    consulta: string;
    clics: number;
    clicsAntes: number;
    impresiones: number;
    posicion: number;
    posicionAntes: number | null;
  }[];
  /** Los enlaces que recibe de fuera: con qué texto, a dónde y de dónde. */
  enlaces: {
    mes: string;
    anclajes: { texto: string; porcentaje: number; backlinks: number }[];
    paises: { pais: string; porcentaje: number; dominios: number }[];
    aLaPortada: number | null;
    aOtrasPaginas: number | null;
  } | null;
  /** Quién enlaza a quién dentro de la propia web. */
  enlazado: {
    dia: string;
    rastreadas: number;
    huerfanas: string[];
    callejones: string[];
    masEnlazadas: { url: string; entrantes: number; salientes: number }[];
    servicios: {
      url: string;
      entrantes: number;
      desde: string[];
      flojo: boolean;
      medida: boolean;
    }[];
  } | null;
  /** Qué páginas dice tener, y qué ha cambiado desde la lectura anterior. */
  sitemap: {
    url: string;
    dia: string;
    cuantas: number;
    nuevas: string[];
    desaparecidas: string[];
  } | null;
  alertas: { gravedad: string; que: string; desde: string }[];
  acciones: {
    id: string;
    queHacer: string;
    porQue: string;
    enJuego: number;
    encargada: boolean;
    prioridad: "alta" | "media" | "baja";
    plazo: string;
  }[];
  pendientes: { id: string; que: string; desde: string }[];
  objetivos: {
    que: string;
    meta: number;
    partida: number | null;
    fechaLimite: string | null;
  }[];
  informe: {
    mes: string;
    generadoEl: string;
    comentario: string | null;
  } | null;
  fuentes: {
    searchConsole: boolean;
    analytics: boolean;
    seRanking: boolean;
    conInformeImportado: boolean;
  };
}

/**
 * Una chispa: la curva en miniatura dentro de la tarjeta.
 *
 * Doce puntos bastan para ver la forma, que es lo único que aporta a ese
 * tamaño. Poner la curva entera ahí la convierte en una mancha.
 */
function Chispa({
  puntos,
  menosEsMejor,
}: {
  puntos: number[];
  menosEsMejor?: boolean;
}) {
  if (puntos.length < 3) return null;

  const alto = 26;
  const ancho = 70;
  const max = Math.max(...puntos);
  const min = Math.min(...puntos);
  const rango = max - min || 1;

  // Con `menosEsMejor` se invierte: si no, una mejora de posición bajaría.
  const y = (v: number) => {
    const norm = (v - min) / rango;
    return (menosEsMejor ? norm : 1 - norm) * alto;
  };

  const d = puntos
    .map((v, i) => `${(i / (puntos.length - 1)) * ancho},${y(v)}`)
    .join(" ");

  // Sube si el último está mejor que el primero, con la regla que toque.
  const mejora = menosEsMejor
    ? puntos.at(-1)! < puntos[0]!
    : puntos.at(-1)! > puntos[0]!;

  return (
    <svg
      className={mejora ? "chispa mejor" : "chispa peor"}
      viewBox={`0 0 ${ancho} ${alto}`}
      aria-hidden
    >
      <polyline points={d} />
    </svg>
  );
}

/**
 * El anillo de salud.
 *
 * Enseña un PERCENTIL, no una nota: el informe dice «mejor que el 84 % de las
 * webs que audita SE Ranking». Va escrito debajo porque un número dentro de un
 * anillo se lee como un aprobado sobre cien, y no lo es — se puede estar por
 * encima del 84 % y tener cincuenta errores, porque casi todas los tienen.
 */
function Anillo({ porcentaje, nota }: { porcentaje: number; nota?: boolean }) {
  const r = 34;
  const vuelta = 2 * Math.PI * r;
  const lleno = (porcentaje / 100) * vuelta;

  return (
    <div className="anillo">
      <svg
        viewBox="0 0 80 80"
        role="img"
        aria-label={`Mejor que el ${porcentaje} por ciento`}
      >
        <circle className="fondo" cx="40" cy="40" r={r} />
        <circle
          className="lleno"
          cx="40"
          cy="40"
          r={r}
          strokeDasharray={`${lleno} ${vuelta}`}
          transform="rotate(-90 40 40)"
        />
        <text x="40" y="45" className="cifra">
          {porcentaje}%
        </text>
      </svg>
      {/*
       * El pie NO es decorativo: dice qué es el número.
       *
       * El de la auditoría es un PERCENTIL —mejor que ese % de las webs que
       * audita SE Ranking— y el de velocidad sí es una nota sobre cien.
       * Pintados iguales y sin pie se leen los dos como un aprobado, y uno de
       * los dos no lo es.
       */}
      <span className="pista">
        {nota
          ? "nota de rendimiento sobre 100"
          : "mejor que ese % de las webs auditadas"}
      </span>
    </div>
  );
}

/**
 * Una Core Web Vital con el umbral de Google, no uno inventado.
 *
 * El umbral es la diferencia entre «va lento» y «va mal para Google», que no
 * es lo mismo y se confunde solo. Por eso va escrito al lado y no sólo en el
 * color.
 */
function Vital({
  que,
  ms,
  limite,
  decimal,
}: {
  que: string;
  ms: number | null;
  limite: number;
  decimal?: boolean;
}) {
  if (ms === null) {
    return (
      <div className="fila-simple">
        <span className="sin">{que}</span>
        <span className="dato sin">sin dato</span>
      </div>
    );
  }
  const bien = ms <= limite;
  const escrito = decimal
    ? ms.toFixed(2).replace(".", ",")
    : ms >= 1000
      ? `${(ms / 1000).toFixed(1).replace(".", ",")} s`
      : `${Math.round(ms)} ms`;

  return (
    <div className="fila-simple">
      <span className={bien ? "" : "peor"}>{que}</span>
      <span className="dato">
        <strong className={bien ? "mejor" : "peor"}>{escrito}</strong>
      </span>
    </div>
  );
}

/** «hace 6 h», «hace 2 días». Una fecha suelta obliga a hacer la resta. */
function hace(fecha: string): string {
  const dias = Math.floor(
    (Date.now() - new Date(`${fecha}T12:00:00Z`).getTime()) / 86_400_000,
  );
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 31) return `hace ${dias} días`;
  const meses = Math.round(dias / 30);
  return `hace ${meses} ${meses === 1 ? "mes" : "meses"}`;
}

/**
 * Una fila de cifras selladas. La ÚNICA forma de pintar una cifra.
 *
 * Existe para que no haya dos. La fila de cabecera y la de Técnico se pintaban
 * con el mismo JSX copiado, y dos copias de un pintado divergen: se añade el
 * porqué de un hueco en una y la otra se queda enseñando un guión pelado.
 *
 * No decide nada. El valor, el estado, el sentido de la flecha y la frase del
 * hueco vienen resueltos de `paraVista`; aquí sólo se colocan.
 */
function FilaDeCifras({
  cifras,
  chispa,
  llevaA,
}: {
  cifras: CifraConAlcance[];
  /** Los puntos de la curva, para la cifra que dibuje una. */
  chispa?: number[];
  /**
   * A qué pestaña lleva cada cifra al pulsarla.
   *
   * Una cifra de cabecera es un titular: dice que algo pasa y no dice por qué.
   * El por qué está siempre en otra pestaña, y obligar a buscarla a mano es
   * poner un escalón entre ver el problema y mirarlo.
   */
  llevaA?: (clave: string) => (() => void) | undefined;
}) {
  if (cifras.length === 0) return null;

  /**
   * La misma advertencia, dicha UNA vez por fila.
   *
   * Cuando la medición de Analytics trae páginas de otras webs, ese aviso le
   * toca a los contactos, a los usuarios y a las visitas: los tres salían con
   * el párrafo entero debajo, tres veces el mismo texto de 235 caracteres en
   * la misma pantalla. Repetido tres veces no avisa el triple, avisa menos —
   * se lee como plantilla y se deja de leer.
   *
   * Se deduplica AQUÍ y no en el servidor: allí no se sabe qué cifras van a
   * salir juntas, y quitarlo de una dejaría muda la pestaña donde esa cifra
   * sale sola.
   */
  const loDiceEntero = new Map<string, string>();
  for (const c of cifras)
    if (c.aviso && !loDiceEntero.has(c.aviso))
      loDiceEntero.set(c.aviso, c.clave);

  return (
    <div className="cifras-con-alcance">
      {cifras.map((c) => {
        const ir = llevaA?.(c.clave);
        const Marco = ir ? "button" : "div";
        return (
          <Marco
            className={`cifra ${c.estado}${ir ? " clicable" : ""}`}
            key={c.clave}
            {...(ir ? { type: "button" as const, onClick: ir } : {})}
          >
            <span className="que">
              {c.etiqueta}
              {/* Cae al alcance si no hay texto propio: sin ayuda no se queda ninguna. */}
              <Ayuda texto={AYUDAS[c.clave] ?? c.alcance} />
            </span>
            {c.estado === "dato" ? (
              /* La nota de salud se pinta de su color: la pregunta que contesta
                 es «¿tengo que mirar esto hoy?», y un número gris no la
                 contesta de un vistazo. */
              <strong
                className={
                  c.clave === "salud"
                    ? `semaforo ${(c.valor ?? 0) >= 70 ? "verde" : (c.valor ?? 0) >= 45 ? "ambar" : "rojo"}`
                    : undefined
                }
              >
                {(c.valor ?? 0).toLocaleString("es-ES")}
              </strong>
            ) : (
              /* Un hueco no es un cero. El porqué va justo debajo. */
              <strong className="hueco">—</strong>
            )}
            {/*
             * Sin base anterior NO se pinta flecha.
             *
             * <<59 ▲ 59>> se lee como que los contactos se han multiplicado, y
             * lo que pasó es que el mes anterior no había con qué comparar. La
             * flecha más llamativa de la pantalla para el único caso en que no
             * hay nada que celebrar.
             */}
            {c.sentido === "desdeCero" && (
              <span className="sin-base">primer mes con dato</span>
            )}
            {c.sentido !== "desdeCero" &&
              c.cambio !== null &&
              c.cambio !== 0 && (
                <span className={c.sentido === "mejor" ? "mejor" : "peor"}>
                  {c.cambio > 0 ? "▲" : "▼"}{" "}
                  {Math.abs(Math.round(c.cambio * 10) / 10).toLocaleString(
                    "es-ES",
                  )}
                </span>
              )}
            {c.clave === "posicionMedia" && chispa && chispa.length > 0 && (
              <Chispa puntos={chispa} menosEsMejor />
            )}
            {/*
             * De qué está hecho el número, debajo del número.
             *
             * <<59 contactos>> no dice si fueron llamadas o formularios, y eso
             * cambia qué se optimiza: 59 llamadas y cero formularios significa
             * que el formulario no funciona o que nadie lo encuentra.
             */}
            {c.partes && c.partes.length > 0 && (
              <span className="partes">
                {c.partes.map((p) => (
                  <span className="parte" key={p.que}>
                    <b>{p.cuantas.toLocaleString("es-ES")}</b> {p.que}
                  </span>
                ))}
              </span>
            )}
            {/*
             * La advertencia va pegada al número, no al final de la pantalla.
             *
             * Una cifra con su advertencia a diez centímetros sigue mintiendo:
             * se lee el número, se saca la conclusión, y ya no se baja.
             */}
            {c.aviso &&
              (loDiceEntero.get(c.aviso) === c.clave ? (
                <span className="aviso-cifra">{c.aviso}</span>
              ) : (
                /* A esta cifra le toca lo mismo, pero ya está dicho arriba. */
                <span className="aviso-cifra breve" title={c.aviso}>
                  ⚠ le afecta el aviso de arriba
                </span>
              ))}
            <span className="alcance">{c.alcance}</span>
            {c.comoSeDice && <span className="porque">{c.comoSeDice}</span>}
            {c.detalleInterno && (
              <span className="detalle">{c.detalleInterno}</span>
            )}
          </Marco>
        );
      })}
    </div>
  );
}

/**
 * Aquí vivía `Tarjeta`, que pintaba la fila de KPI a pelo.
 *
 * Recibía un número y un `falta` escrito a mano en cada llamada, sin alcance y
 * sin estado. «Contactos —» podía significar cuatro cosas distintas —sin
 * Analytics, no se pudo medir, todavía no medíamos, o de verdad ninguno— y las
 * cuatro se veían igual. Ahora todas las cifras entran por `paraVista`, que es
 * lo único que sabe poner el sello que la pantalla exige, y traen su alcance,
 * su estado y su comparación ya resueltos.
 */
/**
 * La curva de posiciones, dibujada a mano en SVG.
 *
 * Sin librería de gráficas: son doce líneas de trigonometría de instituto y
 * evita meter 300 KB en la aplicación para pintar una polilínea.
 *
 * El eje va INVERTIDO —la posición 1 arriba— porque es como se lee una
 * posición. Con el eje normal, una mejora bajaría por la pantalla.
 */
/**
 * Media móvil de siete días.
 *
 * La posición diaria de una keyword pequeña salta diez puestos porque ese día
 * hubo tres impresiones en vez de treinta. Dibujada en crudo no es una
 * tendencia, es ruido — y seis keywords en crudo es una maraña donde no se ve
 * ninguna.
 *
 * Siete días y no tres porque el tráfico tiene forma de semana: promediar
 * media semana deja dentro el escalón del fin de semana.
 */
function suavizar(
  puntos: { dia: string; posicion: number }[],
): { dia: string; posicion: number }[] {
  const ventana = 7;
  return puntos.map((p, i) => {
    const desde = Math.max(0, i - ventana + 1);
    const trozo = puntos.slice(desde, i + 1);
    return {
      dia: p.dia,
      posicion: trozo.reduce((s, x) => s + x.posicion, 0) / trozo.length,
    };
  });
}

function Curva({ curva }: { curva: Datos["curva"] }) {
  /**
   * Cuatro líneas como mucho, y las que mejor posicionan.
   *
   * Seis se pisan entre ellas y ninguna se lee. Las que están en el puesto 40
   * comparten la parte de abajo del gráfico y no se distinguen; las que
   * importan son las que están cerca de arriba.
   */
  const conDatos = curva
    .filter((c) => c.puntos.length > 2)
    .map((c) => ({ keyword: c.keyword, puntos: suavizar(c.puntos) }))
    .sort(
      (a, b) =>
        (a.puntos.at(-1)?.posicion ?? 99) - (b.puntos.at(-1)?.posicion ?? 99),
    )
    .slice(0, 4);
  if (conDatos.length === 0) {
    return (
      <p className="pista">
        Todavía no hay curva. Se recoge sola cada día, y sólo de las keywords de
        servicio que estén en la estrategia.
      </p>
    );
  }

  const ANCHO = 560;
  const ALTO = 180;
  const dias = [
    ...new Set(conDatos.flatMap((c) => c.puntos.map((p) => p.dia))),
  ].sort();
  const peor = Math.min(
    60,
    Math.max(...conDatos.flatMap((c) => c.puntos.map((p) => p.posicion)), 10),
  );

  const x = (dia: string) =>
    (dias.indexOf(dia) / Math.max(1, dias.length - 1)) * ANCHO;
  // Invertido: la posición 1 arriba del todo.
  const y = (pos: number) => (Math.min(pos, peor) / peor) * ALTO;

  return (
    <div className="curva">
      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        preserveAspectRatio="none"
        role="img"
      >
        {conDatos.map((c, i) => (
          <polyline
            key={c.keyword}
            className={`linea l${i % 6}`}
            points={c.puntos
              .map((p) => `${x(p.dia)},${y(p.posicion)}`)
              .join(" ")}
          />
        ))}
      </svg>
      <div className="leyenda">
        {conDatos.map((c, i) => (
          <span key={c.keyword} className={`clave l${i % 6}`}>
            {c.keyword}
          </span>
        ))}
      </div>
      <p className="pista">
        {dias[0]} → {dias.at(-1)} · media de 7 días. La posición 1 va arriba,
        así que una línea que sube es una keyword que mejora.
      </p>
    </div>
  );
}

/**
 * Las ocho secciones, en el orden en que se miran.
 *
 * Estaban todas en una tirada y no se podia leer: para ver la auditoria habia
 * que pasar por encima de las keywords, y para ver la estrategia habia que
 * bajar por delante de todo lo demas. Cada seccion contesta UNA pregunta, y el
 * nombre es esa pregunta abreviada.
 *
 * Las cifras NO entran aqui: se quedan encima de las pestanas, siempre a la
 * vista. Esconder el titular detras de una pestana obliga a un clic para saber
 * lo primero que se quiere saber.
 */
/**
 * Las cinco primeras son las que ve el cliente, en el orden en que se mira una
 * web: cómo va → si hay algo roto → qué está a punto de subir → si eso
 * convierte → si apareces en las IA.
 *
 * Las de después son de gestión —keywords, tareas, ficha del cliente— y sólo
 * salen en la vista interna. No es que se escondan: es que no son suyas. Un
 * cliente abriendo <<Configuración>> no encuentra nada que le sirva y sí
 * bastante que le confunde.
 */
/**
 * Seis pestañas, y ninguna más.
 *
 * Aquí hubo doce —Keywords, Tareas, Estrategia, Publicaciones, Informes,
 * Cliente, Configuración— y no eran del apartado SEO: eran gestión del cliente
 * metida donde se mira cómo va el posicionamiento. Doce pestañas no son más
 * información, son ninguna: nadie las abre todas y la que importa se pierde
 * entre las que no.
 *
 * El orden es el de las preguntas que se hacen al abrir: cómo va → dónde
 * estoy → cuánta gente llega → cuántos contactan → hay algo roto → salgo en
 * las IA.
 */
/**
 * Las dos filas de Resumen, en el orden en que se miran.
 *
 * Arriba lo que contesta <<¿va bien el negocio?>>; debajo, lo que explica por
 * qué. Estuvo al revés —posiciones arriba, contactos abajo— y eso hacía que
 * lo primero que se veía fuera el medio en vez del fin.
 */
const VITALES = [
  "contactos",
  "usuarios",
  "visitas",
  "keywordsTop3",
  "posicionMedia",
  "salud",
];
const DETALLE = [
  "enPrimeraPagina",
  "clics",
  "impresiones",
  "errores",
  "informesConReparos",
];

/** Las cifras de una fila, en el orden pedido y sin colarse ninguna otra. */
function ordenar(
  cifras: CifraConAlcance[],
  orden: string[],
): CifraConAlcance[] {
  return orden
    .map((clave) => cifras.find((c) => c.clave === clave))
    .filter((c): c is CifraConAlcance => c !== undefined);
}

/**
 * A qué pestaña lleva cada cifra.
 *
 * Escrito una vez y usado por las dos filas: con un mapa por fila, añadir una
 * cifra a la de abajo y olvidarse de su destino la deja muerta al clic sin que
 * nada lo diga.
 */
function aSuPestana(ir: (s: Seccion) => void) {
  const donde: Record<string, Seccion> = {
    enPrimeraPagina: "busquedas",
    keywordsTop3: "busquedas",
    posicionMedia: "busquedas",
    clics: "busquedas",
    impresiones: "busquedas",
    visitas: "gente",
    usuarios: "gente",
    contactos: "gente",
    errores: "salud",
    salud: "salud",
  };
  return (clave: string) => {
    const destino = donde[clave];
    return destino ? () => ir(destino) : undefined;
  };
}

/**
 * Cuatro pestañas, y ninguna más.
 *
 * Aquí hubo doce, luego seis, y seis seguían siendo dos de más:
 *
 *   · «Conversiones» existía ENTERA para pintar UNA tarjeta en una rejilla de
 *     seis columnas. Los contactos salen de la misma medición que el tráfico y
 *     del mismo mes; separarlos obligaba a ir y venir para contestar «entró
 *     gente y ¿contactó?», que es una sola pregunta.
 *   · «IA» tiene dos filas en toda la base, de un solo cliente, y su contador
 *     era un `null` fijo. Una pestaña que casi siempre está vacía enseña a no
 *     abrir ninguna.
 *
 * Quedan cuatro, y el orden es el de las preguntas que se hacen al abrir:
 * cómo va → dónde salgo en Google → cuánta gente llega y cuánta contacta →
 * hay algo roto.
 */
const SECCIONES = [
  ["resumen", "Resumen", "El semáforo del proyecto"],
  ["busquedas", "Búsquedas", "Dónde sales en Google y en las IA, qué se pisa y qué está a un empujón"],
  /* Páginas y Competencia, desde el 26/10/2026: Jorge pidió un panel «a la altura de Semrush o Ahrefs». */
  ["paginas", "Páginas", "Cada página de la web: qué tráfico trae, por qué búsquedas y qué le falla"],
  ["competencia", "Competencia", "Quién sale en tu lugar"],
  ["gente", "Gente", "Cuánta llega y cuánta contacta"],
  ["salud", "Salud", "Si hay algo roto"],
] as const;

type Seccion = (typeof SECCIONES)[number][0];

/**
 * El emoji de cada gravedad, con su palabra al lado. Los dos, no uno.
 *
 * Sólo el color no se lee: rojo y ámbar a 11 píxeles son dos grises para quien
 * no distingue esos dos. Y sólo la palabra no se ve: en una lista de doce
 * filas la vista salta antes al color que al texto. Juntos se encuentra de un
 * vistazo y se lee cuando hace falta.
 *
 * Los colores son los del propio informe — SE Ranking pinta el error con un
 * círculo rojo, la advertencia con un triángulo ámbar y el aviso con uno azul —
 * para no tener que traducir entre su pantalla y la nuestra.
 */
const EMOJI_DE_GRAVEDAD: Record<string, string> = {
  error: "🔴",
  advertencia: "🟠",
  aviso: "🔵",
};

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** «2026-07» → «julio 2026». Un mes se lee, no se descifra. */
export function mesLegible(mes: string): string {
  const n = Number(mes.slice(5, 7));
  return `${MESES[n - 1] ?? mes} ${mes.slice(0, 4)}`;
}

/**
 * El calendario que gobierna TODAS las secciones a la vez.
 *
 * Va en la cabecera y no dentro de cada pestaña a propósito: un selector por
 * sección dejaría tener Tráfico en marzo y Salud en agosto, y las dos
 * pantallas diciendo <<ahora>> sin distinguirse en nada. Uno solo arriba
 * significa que lo que se ve es UN momento, entero.
 *
 * Y las flechas sólo saltan a meses que existen en `disponibles`, en vez de
 * restar uno: pasar por cuatro meses vacíos hasta encontrar dato se lee como
 * que el sistema perdió los datos, no como que no los hubo.
 */
/**
 * Por qué se está viendo este mes, dicho en la pantalla.
 *
 * El más frecuente hoy es «el último mes con datos»: sólo un cliente de los
 * sesenta y cinco tiene informe congelado. Cuando se generen para todos, el que
 * mandará será el primero.
 */
const PORQUE_DEL_MES: Record<Datos["calendario"]["porQue"], string> = {
  "ultimo-informe": " · el mes del último informe",
  "ultimo-dato": " · el último mes con datos",
  elegido: " · lo has elegido tú",
  "mes-en-curso": " · el mes en curso, todavía a medias",
  "sin-datos": " · todavía no hay nada medido de este cliente",
};

/**
 * Qué trae un mes, en dos palabras, para el desplegable.
 *
 * Elegir un mes a ciegas y encontrarse media pantalla en blanco se lee como
 * una avería. Decirlo antes convierte el hueco en información.
 */
function queTrae(tiene: Datos["calendario"]["disponibles"][number]["tiene"]): string {
  if (tiene.informe) return "informe";
  const hay = [
    tiene.analytics && "tráfico",
    tiene.posiciones && "posiciones",
    tiene.auditoria && "auditoría",
  ].filter(Boolean) as string[];
  if (hay.length === 0) return "sin datos";
  // Con varias fuentes el mes se sostiene solo; nombrarlas sería ruido.
  return hay.length === 1 ? `sólo ${hay[0]}` : "";
}

function SelectorDeMes({
  calendario,
  onCambiar,
}: {
  calendario: Datos["calendario"];
  onCambiar: (mes: string | null) => void;
}) {
  // Vienen del más nuevo al más viejo.
  const i = calendario.disponibles.findIndex((m) => m.mes === calendario.mes);
  const anterior = i >= 0 ? calendario.disponibles[i + 1]?.mes : undefined;
  const siguiente = i > 0 ? calendario.disponibles[i - 1]?.mes : undefined;

  return (
    <div className="selector-mes">
      <button
        type="button"
        onClick={() => anterior && onCambiar(anterior)}
        disabled={!anterior}
        title={
          anterior
            ? `Ver ${mesLegible(anterior)}`
            : "No hay nada guardado antes de este mes"
        }
        aria-label="Mes anterior"
      >
        ‹
      </button>

      <select
        value={calendario.mes}
        onChange={(e) => onCambiar(e.target.value)}
        aria-label="Mes que se está viendo"
      >
        {calendario.disponibles.map((m) => {
          const trae = queTrae(m.tiene);
          return (
            <option key={m.mes} value={m.mes}>
              {mesLegible(m.mes)}
              {trae && ` · ${trae}`}
            </option>
          );
        })}
      </select>

      <button
        type="button"
        onClick={() => siguiente && onCambiar(siguiente)}
        disabled={!siguiente}
        title={siguiente ? `Ver ${mesLegible(siguiente)}` : "Ya estás en el último mes"}
        aria-label="Mes siguiente"
      >
        ›
      </button>

      {/*
       * La salida, mientras se esté en un mes elegido a mano.
       *
       * Sin ella se vuelve con las flechas, contando meses hacia delante — y el
       * riesgo de verdad no es la incomodidad: es dejarse el panel en marzo,
       * volver mañana y leer las cifras de marzo como si fueran de hoy.
       *
       * Ya no dice «al mes actual»: el sitio al que devuelve es el mes que el
       * panel abre solo, que es el del último informe y no el del calendario.
       */}
      {calendario.porQue === "elegido" && (
        <button
          type="button"
          className="volver-al-mes"
          onClick={() => onCambiar(null)}
        >
          Volver
        </button>
      )}
    </div>
  );
}

/**
 * Un conmutador, no dos paneles.
 *
 * Lo que cambia es qué filas se ven y cómo se dice un hueco. Los números son
 * los mismos en las dos vistas — hay una prueba en `vista.ts` que lo comprueba
 * sobre todas a la vez, para que nadie tenga que acordarse.
 *
 * Fija en interna, sin conmutador en pantalla: esta pantalla es la herramienta
 * de gestión de Jorge, se abre siempre en interna, y un interruptor que nadie
 * va a mover ocupa el sitio de algo que sí se usa. La maquinaria sigue entera
 * —la ruta acepta `?vista=cliente` y la prueba de que las dos dicen lo mismo
 * sigue en pie—: quitar el botón esconde un camino, borrarlo obligaría a
 * rehacerlo el día del informe del cliente.
 */
const VISTA = "interna" as const;

export interface SeoDelMes {
  datos: Datos | null;
  cargando: boolean;
  /** El mes PEDIDO. `null` = el que el servidor elija por defecto. */
  mes: string | null;
  setMes: (mes: string | null) => void;
  recargar: () => void;
}

/**
 * El SEO de un cliente en un mes, cargado UNA VEZ para las tres pantallas.
 *
 * Vive aquí arriba y no dentro del panel de métricas porque las tres pantallas
 * —cómo va, informes, el cliente— comparten cabecera y selector de mes. Si cada
 * una cargara lo suyo, elegir marzo en una y saltar a otra enseñaría dos meses
 * distintos con el mismo selector arriba diciendo uno solo.
 *
 * Se guarda el mes PEDIDO, no el servido: el servidor puede corregirlo —un mes
 * futuro, un formato raro— y lo que se pinta en el selector sale de
 * `datos.calendario.mes`, que es el que de verdad se usó para las cifras.
 */
export function useSeoDelMes(clienteId: string): SeoDelMes {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [mes, setMes] = useState<string | null>(null);

  const url = `/api/memoria/clientes/${clienteId}/panel-seo?vista=${VISTA}${
    mes ? `&mes=${mes}` : ""
  }`;

  /**
   * Vuelve a pedir el panel entero después de tocar algo.
   *
   * Se recarga todo y no sólo lo tocado a propósito: marcar una acción cambia
   * también los contadores de las pestañas y lo que sale en «Pendiente».
   * Actualizar a mano cada trozo es como se acaba enseñando un panel que dice
   * dos cosas distintas a la vez.
   */
  const recargar = useCallback(() => {
    void fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Datos | null) => d && setDatos(d))
      .catch(() => {});
  }, [url]);

  useEffect(() => {
    setCargando(true);
    setDatos(null);
    void fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Datos | null) => setDatos(d))
      .catch(() => setDatos(null))
      .finally(() => setCargando(false));
  }, [url]);

  /*
   * Cambiar de cliente suelta el mes elegido.
   *
   * Los meses que tenía uno no tienen por qué existir en el siguiente, y
   * quedarse en marzo de otro cliente enseñaría una pantalla vacía que parece
   * una avería. Con `null`, el servidor vuelve a elegir el que toque: el del
   * último informe de ESTE cliente.
   */
  useEffect(() => setMes(null), [clienteId]);

  return { datos, cargando, mes, setMes, recargar };
}

/**
 * Las tres pantallas de un cliente, y la pregunta que contesta cada una.
 *
 * Antes esto eran TRECE pestañas al mismo nivel: seis de métricas encima de
 * siete de gestión, sin nada que dijera cuál se mira cada día y cuál se toca
 * una vez al trimestre. Jorge lo dijo así: «hay mil cosas y es un desastre».
 *
 * Tres entradas, y cada una responde algo distinto: cómo va este mes, qué se
 * le cuenta al cliente, y dónde se rellena lo suyo.
 */
export type VistaDelCliente = "como-va" | "estrategia" | "informes" | "cliente";

const VISTAS = [
  ["como-va", "Cómo va", "Los números de este mes"],
  /* Aparte desde el 20/10/2026: estaba dentro de «Informes» y Jorge no la encontraba. */
  ["estrategia", "Estrategia", "Qué se ataca y por qué, y qué toca ahora"],
  ["informes", "Informes", "Lo que se le cuenta al cliente, mes a mes"],
  ["cliente", "El cliente", "Keywords, publicaciones, ficha y conexiones"],
] as const satisfies readonly (readonly [VistaDelCliente, string, string])[];

/**
 * La cabecera común: quién, qué mes, y a cuál de las tres pantallas se va.
 *
 * Va por encima de las tres y no dentro de ninguna, porque el selector de mes
 * manda sobre todas a la vez. Uno por pantalla dejaría tener las métricas en
 * marzo y el informe en agosto, las dos diciendo «ahora» sin distinguirse.
 */
export function CabeceraSeo({
  nombreCliente,
  calendario,
  onMes,
  vista,
  onVista,
}: {
  nombreCliente: string;
  /**
   * `null` mientras carga o si la carga falló.
   *
   * La cabecera se pinta igual: si sólo saliera con datos, un panel que falla
   * dejaría sin barra de navegación y no habría forma de llegar a la ficha ni
   * a las conexiones, que es justo lo que se va a mirar cuando algo no carga.
   */
  calendario: Datos["calendario"] | null;
  onMes: (mes: string | null) => void;
  vista: VistaDelCliente;
  onVista: (v: VistaDelCliente) => void;
}) {
  return (
    <>
      <header className="cabecera-seo">
        <div>
          <h2>SEO · {nombreCliente}</h2>
          {/*
           * UN mes, y por qué es ése.
           *
           * Aquí se pintaban TRES meses a la vez —el de la última captura, el
           * del informe y el del selector— y encima se atribuía el tráfico al
           * informe congelado cuando sale de las métricas mensuales. Tres
           * fechas en una frase no informan: hacen desconfiar de la pantalla.
           *
           * Ahora manda el selector, y lo único que hay que explicar es por qué
           * se ha abierto en ese mes. Sin la explicación, entrar en julio
           * estando en agosto parece que el sistema se quedó atrás.
           */}
          <p className="pista">
            {calendario
              ? `${mesLegible(calendario.mes)}${PORQUE_DEL_MES[calendario.porQue]}`
              : "Cargando…"}
          </p>
        </div>
        {calendario && (
          <SelectorDeMes calendario={calendario} onCambiar={onMes} />
        )}
      </header>

      <nav className="vistas-seo">
        {VISTAS.map(([id, nombre, ayuda]) => (
          <button
            key={id}
            type="button"
            className={vista === id ? "vista activa" : "vista"}
            onClick={() => onVista(id)}
            title={ayuda}
            aria-current={vista === id}
          >
            {nombre}
          </button>
        ))}
      </nav>

      {/*
       * La franja sólo cuando el mes se ELIGIÓ, no siempre que no sea hoy.
       *
       * Con el mes por defecto puesto en el del último informe, `esElActual` es
       * falso casi siempre: la franja saldría en cada carga y en dos días
       * dejaría de leerse, incluido el día que avisa de algo. El caso que hay
       * que señalar es haberse ido a un mes a propósito y olvidarlo puesto.
       */}
      {calendario?.porQue === "elegido" && (
        <p className="mirando-atras">
          Estás viendo cómo estaba en{" "}
          <strong>{mesLegible(calendario.mes)}</strong>, porque lo has elegido
          en el selector.
        </p>
      )}
    </>
  );
}

export function PanelSeo({
  clienteId,
  datos,
  recargar,
}: {
  clienteId: string;
  datos: Datos;
  recargar: () => void;
}) {
  const [seccion, setSeccion] = useState<Seccion>("resumen");
  /** Qué botón está esperando respuesta, para no poder pulsarlo dos veces. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  /*
   * Al cambiar de cliente se vuelve al resumen. Quedarse en «Salud» porque era
   * lo último que se miró del anterior abre el cliente nuevo por su rincón más
   * aburrido.
   */
  useEffect(() => setSeccion("resumen"), [clienteId]);

  /** Manda una orden y recarga. Devuelve el error para poder enseñarlo. */
  const mandar = useCallback(
    async (
      ruta: string,
      metodo: string,
      cuerpo?: unknown,
    ): Promise<string | null> => {
      setOcupado(ruta);
      try {
        const r = await fetch(`/api/memoria/clientes/${clienteId}${ruta}`, {
          method: metodo,
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
        });
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string };
          return j.error ?? "No se ha podido.";
        }
        recargar();
        return null;
      } finally {
        setOcupado(null);
      }
    },
    [clienteId, recargar],
  );

  /*
   * Que falle el panel no borra al cliente.
   *
   * Quién es y cómo está conectado no salen de esta llamada, así que se siguen
   * pudiendo abrir: son justo lo que se va a mirar cuando algo no carga. Los
   * pinta `SeoDelCliente`, que es de quien son, y por eso aquí ya no se
   * reciben — recibirlos hacía que al fallar el panel con «Ficha» abierta se
   * pintara DOS VECES, cada copia con su estado de edición guardando contra la
   * misma ruta.
   */
  if (!datos)
    return (
      <p className="pista">
        No se ha podido cargar el SEO de este cliente. Los apartados de abajo
        —Ficha, Conexiones— siguen funcionando.
      </p>
    );

  const { cifras, fuentes } = datos;
  const deServicio = datos.keywords.filter((k) => k.tipo === "servicio");

  /**
   * La media de posiciones por día, para la chispa de la tarjeta.
   *
   * Se saca de las mismas curvas que ya vienen: pedirla aparte sería otra
   * consulta para dibujar setenta píxeles.
   */
  const porDia = new Map<string, number[]>();
  for (const c of datos.curva) {
    for (const p of c.puntos)
      porDia.set(p.dia, [...(porDia.get(p.dia) ?? []), p.posicion]);
  }
  const mediaPorDia = [...porDia.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([, v]) => v.reduce((s, n) => s + n, 0) / v.length);

  /**
   * Cuantas cosas hay dentro de cada seccion, para el numerito de la pestana.
   *
   * Una pestana sin numero es una pestana que no hace falta abrir. Sin esto,
   * repartir el panel en ocho lo unico que consigue es esconder las alertas
   * detras de un clic que nadie sabe que tiene que dar.
   *
   * `null` es «no hay nada que contar aqui», no «cero»: en Estrategia o
   * Cliente un cero no querria decir nada.
   */
  /**
   * Los problemas técnicos que ya se pasaron a tareas.
   *
   * Se compara por el texto que genera el botón. Sin esto, el botón sale
   * siempre y volver a pulsarlo da la sensación de que no hizo nada la primera
   * vez —aunque el servidor no la duplique—.
   */
  const yaEsTarea = new Set(
    [...datos.acciones, ...datos.pendientes.map((p) => ({ queHacer: p.que }))]
      .map((a) => a.queHacer)
      .filter((q) => q.startsWith("Arreglar: "))
      .map((q) => q.slice("Arreglar: ".length)),
  );

  /** Lo que espera a que alguien lo coja. Lo encargado va en su propia caja. */
  const sinEncargar = datos.acciones.filter((a) => !a.encargada);

  /**
   * El numerito de cada pestaña cuenta UNA cosa: cuántas hay que mirar.
   *
   * Antes el de Rankings sumaba las que suben y las que bajan, así que
   * cuarenta keywords subiendo pintaban el mismo número que cuarenta
   * desplomándose — y el número que se pone en rojo al lado de un nombre se lee
   * como una alarma. Ahora cuenta sólo las que han bajado, que es lo que hay
   * que ir a ver. Y los otros tres dejan de ser `null` fijos.
   */
  const avisos: Record<Seccion, number | null> = {
    resumen: null,
    busquedas: datos.rankings.titulares?.bajan || null,
    paginas: null,
    competencia: null,
    gente: datos.conversiones.seRompieron.length || null,
    salud: datos.auditoria.errores || null,
  };

  return (
    <div className="panel-seo">
      {/*
       * Cada cifra con su alcance debajo y, si hay hueco, el porqué.
       *
       * Un número sin su alcance es un número correcto afirmando otra cosa, y
       * un hueco sin su frase se lee como un fallo nuestro aunque sea el
       * límite de una fuente.
       */}
      {/**
       * Las pestañas, y las cifras DENTRO de Resumen.
       *
       * Estuvieron sueltas encima de la barra, y entre eso y la fila de
       * fuentes la pantalla tenía dos cabeceras antes de empezar. Ahora la
       * cabecera es una y las cifras abren Resumen, que es de lo que hablan.
       */}
      <nav className="secciones-seo">
        {SECCIONES.map(([id, nombre, ayuda]) => (
          <button
            key={id}
            type="button"
            className={seccion === id ? "seccion activa" : "seccion"}
            onClick={() => setSeccion(id)}
            title={ayuda}
          >
            {nombre}
            {/* El número de lo que hay dentro, para no tener que abrir la
                pestaña sólo para descubrir que está vacía. */}
            {avisos[id] !== null && <span className="n">{avisos[id]}</span>}
          </button>
        ))}
      </nav>

      {seccion === "resumen" && (
        <>
          {/*
           * Dos filas, y el orden importa: arriba lo que dice si el NEGOCIO va.
           *
           * Contactos, usuarios y visitas encabezan porque son el titular;
           * las posiciones son el medio, no el fin. Estuvieron abajo y
           * vacías a la vez, que es la peor combinación posible: lo que más
           * importa, escondido y sin dato.
           */}
          <FilaDeCifras
            cifras={ordenar(datos.cifrasConAlcance, VITALES)}
            chispa={mediaPorDia}
            llevaA={aSuPestana(setSeccion)}
          />
          <FilaDeCifras
            cifras={ordenar(datos.cifrasConAlcance, DETALLE)}
            llevaA={aSuPestana(setSeccion)}
          />
          <div className="rejilla-panel">
            <Caja
              titulo="Cómo va la posición media"
              fuente="SE Ranking"
              ayuda="La posición media de todas tus búsquedas seguidas, mes a mes. Cuanto más abajo el número, mejor: la posición 1 es la primera de Google. Por eso la línea sube cuando mejoras."
              pie={
                <p className="pista">
                  Cada punto dice sobre cuántas búsquedas se calculó. Una media
                  de 8 sobre tres búsquedas y otra de 12 sobre doscientas no son
                  comparables, y en un gráfico se ven igual de firmes.
                </p>
              }
            >
              <Serie
                puntos={datos.paraPintar.serie.map((x) => ({
                  mes: x.mes,
                  valor: x.posicionMedia,
                  sobre: x.keywords,
                }))}
                menosEsMejor
                formato={(v) => `posición ${v}`}
              />
            </Caja>

            <Caja
              titulo="Día a día, búsqueda a búsqueda"
              fuente="Search Console"
              ayuda="Las búsquedas que seguimos de cerca, con su posición cada día. Suavizada a siete días porque el dato diario salta mucho: un día con tres visitas y otro con treinta dan posiciones muy distintas sin que haya cambiado nada."
            >
              <Curva curva={datos.curva} />
            </Caja>

            <div className="columna-corta">
              <section className="caja-seo">
                <header>
                  <h3>Alertas</h3>
                  {datos.alertas.length > 0 && (
                    <span className="pill peor">{datos.alertas.length}</span>
                  )}
                </header>
                {datos.alertas.length === 0 ? (
                  <p className="pista">
                    Nada abierto. Se mira solo cada día contra Search Console,
                    así que esto está comprobado hoy — no es que nadie haya
                    mirado.
                  </p>
                ) : (
                  datos.alertas.map((a) => (
                    <div className="alerta" key={a.que}>
                      <span
                        className={
                          a.gravedad === "grave" ? "punto grave" : "punto"
                        }
                      />
                      <span>{a.que}</span>
                      <span className="sin">{hace(a.desde)}</span>
                    </div>
                  ))
                )}
              </section>

              <section className="caja-seo">
                <header>
                  <h3>Objetivos</h3>
                </header>
                {datos.objetivos.length === 0 ? (
                  <p className="pista">
                    Ninguno fijado. Sin objetivo se puede decir qué ha pasado,
                    pero no si era suficiente.
                  </p>
                ) : (
                  datos.objetivos.map((o) => (
                    <div className="fila-simple" key={o.que}>
                      <span>{o.que}</span>
                      <span className="dato">
                        {/*
                         * Sin número de partida no es un dato que falte: es un
                         * objetivo que ya nace sin poder comprobarse. Dentro de
                         * tres meses no habrá forma de decir si se cumplió.
                         */}
                        {o.partida === null ? (
                          <em
                            className="hueco"
                            title="Sin número de partida no se podrá comprobar si se cumple"
                          >
                            sin partida
                          </em>
                        ) : (
                          o.partida
                        )}{" "}
                        → <strong>{o.meta}</strong>
                      </span>
                    </div>
                  ))
                )}
              </section>
            </div>

            <section className="caja-seo">
              <header>
                <h3>Búsquedas que traen clics</h3>
                <span className="pista">
                  Lo que escribe la gente, no lo que perseguimos. Suelen ser
                  otras.
                </span>
              </header>
              {datos.busquedasQueTraen.length === 0 ? (
                <p className="pista">
                  {cifras.delMes
                    ? `El informe de ${cifras.delMes} se generó antes de que esto se guardara. Saldrá solo en el del mes que viene, o pídele a BILBOBOT que rehaga el de este mes.`
                    : "Sin informe todavía. Salen del informe mensual, congeladas con él."}
                </p>
              ) : (
                <div className="tabla-panel">
                  <div className="cabecera-panel cinco">
                    <span>Búsqueda</span>
                    <span>Clics</span>
                    <span>Antes</span>
                    <span>Impresiones</span>
                    <span>Puesto</span>
                  </div>
                  {datos.busquedasQueTraen.map((b) => {
                    const gana = b.clics - b.clicsAntes;
                    return (
                      <div className="fila-panel cinco" key={b.consulta}>
                        <span className="nombre">{b.consulta}</span>
                        <span className="dato">
                          <strong>{b.clics}</strong>
                        </span>
                        <span className="dato">
                          {gana === 0 ? (
                            "="
                          ) : (
                            <span className={gana > 0 ? "mejor" : "peor"}>
                              {gana > 0 ? "▲" : "▼"} {Math.abs(gana)}
                            </span>
                          )}
                        </span>
                        <span className="dato">
                          {b.impresiones.toLocaleString("es-ES")}
                        </span>
                        <span className="dato">{b.posicion}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {/*
       * BÚSQUEDAS = dónde sales, en Google y en las IA.
       *
       * Google primero porque es de donde viene el negocio hoy, y las IA
       * plegadas al final: se miden dos filas en toda la base y de un solo
       * cliente, así que abrirlas por defecto sería dedicar media pantalla a
       * un «todavía no se ha medido» para sesenta y cuatro de sesenta y cinco.
       */}
      {seccion === "busquedas" && <Rankings datos={datos.rankings} />}

      {seccion === "busquedas" && datos.paraPintar.tramos.length > 0 && (
        <Caja
          titulo="Cómo se reparten las keywords seguidas, mes a mes"
          fuente="SE Ranking"
          ayuda="Cuántas de las keywords que sigue SE Ranking están en el top 3, del 4 al 10, del 11 al 30 y más abajo, cada mes. Del 1 al 3 se lleva casi todos los clics: que crezca esa franja es lo que dice que el trabajo funciona."
        >
          <Tramos meses={datos.paraPintar.tramos} />
        </Caja>
      )}

      {seccion === "busquedas" && <BusquedasAFondo clienteId={clienteId} mes={datos.calendario.mes} />}

      {seccion === "paginas" && <PaginasDelCliente clienteId={clienteId} mes={datos.calendario.mes} />}

      {seccion === "competencia" && <CompetenciaDelCliente clienteId={clienteId} />}

      {seccion === "busquedas" && (
        <details className="caja-plegable" open={datos.ia.medidas > 0}>
          <summary>
            Cuando le preguntan a una inteligencia artificial
            {datos.ia.medidas === 0 ? (
              <span className="pista"> — sin medir todavía</span>
            ) : (
              <span className="n">
                {Math.round((datos.ia.sales / datos.ia.medidas) * 100)}%
              </span>
            )}
          </summary>
        <div className="rejilla-panel una-sola">
          {datos.ia.medidas === 0 ? (
            <Caja
              titulo="¿Apareces cuando le preguntan a una IA?"
              fuente="GEO"
              ayuda="Cada vez más gente pregunta a ChatGPT o Perplexity en vez de buscar en Google. Esto mide si tu web sale nombrada en esas respuestas."
            >
              <p className="grafico-vacio">
                Todavía no se ha medido ninguna consulta para este cliente. Se
                mide preguntando a los modelos por las búsquedas que ya
                seguimos, y se guarda mes a mes como todo lo demás.
              </p>
            </Caja>
          ) : (
            <>
              <Caja
                titulo="¿Apareces cuando le preguntan a una IA?"
                fuente="GEO"
                ayuda="Cada vez más gente pregunta a ChatGPT o Perplexity en vez de buscar en Google. Esto mide si tu web sale nombrada en esas respuestas. Casi ninguna agencia lo mide todavía."
                pie={
                  <p className="pista">
                    Medido sobre <b>{datos.ia.consultas.length}</b>{" "}
                    {datos.ia.consultas.length === 1 ? "consulta" : "consultas"}{" "}
                    y <b>{datos.ia.motores.length}</b>{" "}
                    {datos.ia.motores.length === 1 ? "motor" : "motores"}, en{" "}
                    {datos.ia.mes}.{" "}
                    <em>
                      Ampliar esa lista hará visibles más huecos: lo que no se
                      pregunta, no se sabe.
                    </em>
                  </p>
                }
              >
                <p className="dato-grande">
                  {Math.round((datos.ia.sales / datos.ia.medidas) * 100)}%
                  <span className="pista">
                    {" "}
                    — sales en {datos.ia.sales} de {datos.ia.medidas} respuestas
                    medidas
                  </span>
                </p>

                <div className="tabla-envoltura">
                  <table className="matriz-ia">
                    <thead>
                      <tr>
                        <th>Consulta</th>
                        {datos.ia.motores.map((m) => (
                          <th key={m} className="num">
                            {m}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {datos.ia.consultas.map((c) => (
                        <tr key={c.keyword}>
                          <td className="kw">{c.keyword}</td>
                          {c.porMotor.map((m) => (
                            <td key={m.modelo} className="celda-ia">
                              {!m.medido ? (
                                /* Sin medir NO es <<no sales>>: es que nadie lo miró. */
                                <span
                                  className="ia-sin"
                                  title="No se ha preguntado en este motor"
                                >
                                  —
                                </span>
                              ) : m.mencionado ? (
                                <span className="ia-si">
                                  sí
                                  {m.posicion !== null && (
                                    <b>
                                      {" "}
                                      {m.posicion}
                                      {m.de !== null ? `/${m.de}` : ""}
                                    </b>
                                  )}
                                </span>
                              ) : (
                                <span className="ia-no">no</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Caja>

              {datos.ia.series.length > 1 && (
                <Caja
                  titulo="Cómo va el porcentaje"
                  fuente="GEO"
                  ayuda="Qué parte de las consultas medidas te nombran, mes a mes. Sube cuando hay contenido que los modelos citan: respuestas claras, datos propios, páginas que responden una pregunta concreta."
                >
                  <Serie
                    puntos={datos.ia.series.map((x) => ({
                      mes: x.mes,
                      valor: x.porcentaje,
                    }))}
                    formato={(v) => `${v}%`}
                  />
                </Caja>
              )}
            </>
          )}
        </div>
        </details>
      )}

      {/*
       * GENTE = cuánta llega y cuánta contacta, que es UNA pregunta.
       *
       * Estaban en dos pestañas —«Tráfico» y «Conversiones»— y salen de la
       * misma medición y del mismo mes. Separarlas obligaba a ir y volver para
       * contestar «¿entró gente y contactó alguien?».
       */}
      {seccion === "gente" && (
        <div className="rejilla-panel una-sola">
          {/*
           * El aviso va ARRIBA del todo, antes que ninguna cifra.
           *
           * Un formulario que dejó de enviar no se ve en un total: el total
           * baja y parece un mes flojo. Lo que delata la avería es que un
           * evento que venía entrando cada mes se ponga a cero de golpe. Y es
           * lo único de esta pantalla que exige hacer algo hoy.
           */}
          {datos.conversiones.seRompieron.length > 0 && (
            <div className="se-rompio">
              <strong>Algo dejó de entrar</strong>
              <ul>
                {datos.conversiones.seRompieron.map((r) => (
                  <li key={r.evento}>
                    <span className="mono">{r.evento}</span> venía entrando unas{" "}
                    <b>{r.solia}</b> veces al mes durante {r.meses} meses y este
                    mes está a cero. Suele ser un formulario que dejó de enviar
                    o un botón que perdió su medición.
                  </li>
                ))}
              </ul>
            </div>
          )}

          <FilaDeCifras
            cifras={ordenar(datos.cifrasConAlcance, [
              "contactos",
              "visitas",
              "usuarios",
              "clics",
              "impresiones",
            ])}
            llevaA={aSuPestana(setSeccion)}
          />

          <Caja
            titulo="Cómo evoluciona el tráfico"
            fuente="Analytics"
            ayuda="Visitas mes a mes. La línea viva es lo que trae la búsqueda; la apagada, el total de la web. Van separadas porque el total sube cuando alguien manda una newsletter o hace una campaña, y eso no lo trajo el SEO."
          >
            <SerieDoble
              lineas={[
                {
                  nombre: "de búsqueda",
                  puntos: datos.trafico.serie.map((m) => ({
                    mes: m.mes,
                    valor: m.organicas,
                    aMedias: m.aMedias,
                  })),
                },
                {
                  nombre: "todo el tráfico",
                  tenue: true,
                  puntos: datos.trafico.serie.map((m) => ({
                    mes: m.mes,
                    valor: m.sesiones,
                    aMedias: m.aMedias,
                  })),
                },
              ]}
            />
          </Caja>

          <Caja
            titulo="De dónde viene la gente"
            fuente="Analytics"
            ayuda="El reparto por canal del último mes cerrado. Sirve para saber qué parte del tráfico depende del SEO y cuánta llega por otras vías — y si aparece <<AI Assistant>>, es gente que llega desde ChatGPT o similares."
            pie={
              datos.trafico.mes ? (
                <p className="pista">Del mes de {datos.trafico.mes}.</p>
              ) : undefined
            }
          >
            <BarrasH
              barras={datos.trafico.canales.map((c) => ({
                etiqueta: c.canal,
                valor: c.sesiones,
              }))}
            />
          </Caja>
        </div>
      )}

      {/* Lo de contactar, en la misma pantalla que lo de llegar. */}
      {seccion === "gente" && (
        <div className="rejilla-panel una-sola">
          <Caja
            titulo="Contactos mes a mes"
            fuente="Analytics"
            ayuda="Cuánta gente que llegó buscando acabó contactando: formularios enviados, llamadas pulsadas, WhatsApp. Es la cifra que más se parece a dinero."
            pie={
              datos.conversiones.tasa !== null ? (
                <p className="pista">
                  De cada cien visitas de búsqueda,{" "}
                  <b>{datos.conversiones.tasa}</b> acaban en contacto
                  {datos.conversiones.tasaAntes !== null && (
                    <>
                      {" "}
                      · el mes anterior fueron {datos.conversiones.tasaAntes}
                    </>
                  )}
                  .
                </p>
              ) : undefined
            }
          >
            <SerieDoble
              lineas={[
                {
                  nombre: "contactos",
                  puntos: datos.conversiones.serie.map((m) => ({
                    mes: m.mes,
                    valor: m.total,
                    aMedias: m.aMedias,
                  })),
                },
              ]}
            />
          </Caja>

          <Caja
            titulo="Por dónde contactan"
            fuente="Analytics"
            ayuda="Separado por tipo porque cambia qué se optimiza: muchas llamadas y ningún formulario significa que el formulario no funciona o que nadie lo encuentra."
          >
            <BarrasH
              barras={datos.conversiones.porTipo.map((t) => ({
                etiqueta: t.que,
                valor: t.cuantas,
              }))}
            />
          </Caja>
        </div>
      )}

      {seccion === "salud" && (
        <div className="rejilla-panel una-sola">
          {/*
           * La nota encabeza la pestaña: es la respuesta a <<¿tengo que mirar
           * esto hoy?>>, y lo demás de aquí es el desglose de por qué.
           */}
          <FilaDeCifras
            cifras={ordenar(datos.cifrasConAlcance, ["salud", "errores"])}
          />
          <Caja
            titulo="Dónde están los problemas técnicos"
            fuente="SE Ranking"
            ayuda="Los problemas que encontró la última auditoría, agrupados por tipo y contados por páginas afectadas. No todos pesan igual: mil páginas con un aviso de CSS molestan menos que diez con un enlace roto."
          >
            <BarrasH
              barras={datos.paraPintar.problemasPorCategoria
                .slice(0, 7)
                .map((c) => ({
                  etiqueta: c.categoria,
                  valor: c.paginas,
                }))}
              sufijo=" pág."
            />
          </Caja>

          {/*
           * Las cifras de Técnico, por la misma puerta que las de cabecera.
           *
           * Van arriba porque son las que dicen si lo de debajo se puede leer:
           * sin clave de PageSpeed la caja de Velocidad no está vacía porque
           * la web vaya mal, y eso hay que decirlo antes de enseñarla vacía.
           */}
          <FilaDeCifras cifras={datos.tecnicoConAlcance} />
          {/* La historia de los enlaces, mes a mes (26/10/2026): llegaba al panel y no se pintaba. */}
          {datos.paraPintar.enlacesPorMes.length > 1 && (
            <Caja
              titulo="Dominios que te enlazan, mes a mes"
              fuente="SE Ranking"
              ayuda="Cuántos dominios distintos enlazan a la web cada mes. Es lo que más pesa de los enlaces: cien enlaces de una sola web cuentan mucho menos que diez de diez webs distintas. Si baja, se han perdido enlaces."
            >
              <SerieDoble
                lineas={[
                  { nombre: "Dominios de referencia", puntos: datos.paraPintar.enlacesPorMes.map((e) => ({ mes: e.mes, valor: e.dominios })) },
                  { nombre: "Domain Trust", tenue: true, puntos: datos.paraPintar.enlacesPorMes.map((e) => ({ mes: e.mes, valor: e.confianza })) },
                ]}
              />
            </Caja>
          )}
          <section className="caja-seo">
            <header>
              <h3>Enlaces que recibes</h3>
              {datos.enlaces && (
                <span className="pill">{datos.enlaces.mes}</span>
              )}
              <span className="pista">
                De fuera, del informe de SE Ranking.
              </span>
            </header>

            {!datos.enlaces ? (
              <p className="pista">
                El informe de este mes no traía las secciones de enlaces —llegan
                con la plantilla que incluye Backlinks, Anclajes e
                IPs/Subredes—. Los recuentos y su estado están arriba.
              </p>
            ) : (
              <>
                {/*
                 * El reparto va primero: es lo que más dice de los tres.
                 *
                 * Con todo apuntando a la portada, las páginas de servicio —las
                 * que venden— compiten sin un solo enlace externo detrás.
                 */}
                {datos.enlaces.aLaPortada !== null && (
                  <>
                    <div className="resumen-auditoria">
                      <span>
                        <strong>{datos.enlaces.aLaPortada}</strong> a la portada
                      </span>
                      <span
                        className={
                          (datos.enlaces.aOtrasPaginas ?? 0) < 10 ? "peor" : ""
                        }
                      >
                        <strong>{datos.enlaces.aOtrasPaginas ?? 0}</strong> al
                        resto de páginas
                      </span>
                    </div>
                    {(datos.enlaces.aOtrasPaginas ?? 0) < 10 && (
                      <p className="pista">
                        Casi todo apunta a la portada. Las páginas de servicio
                        compiten sin enlaces externos detrás, y son las que
                        tienen que vender.
                      </p>
                    )}
                  </>
                )}

                {datos.enlaces.anclajes.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">Con qué texto te enlazan</span>
                      {datos.enlaces.anclajes[0]!.porcentaje >= 30 && (
                        <span className="pill peor">
                          {datos.enlaces.anclajes[0]!.porcentaje} %
                        </span>
                      )}
                      <span className="dato">
                        {datos.enlaces.anclajes.length}
                      </span>
                    </summary>
                    {datos.enlaces.anclajes[0]!.porcentaje >= 30 && (
                      <p className="como">
                        El {datos.enlaces.anclajes[0]!.porcentaje} % de los
                        enlaces dice «{datos.enlaces.anclajes[0]!.texto}». Un
                        perfil tan cargado hacia un texto comercial no parece
                        natural — conviene mirarlo antes de conseguir más
                        enlaces iguales.
                      </p>
                    )}
                    {datos.enlaces.anclajes.map((a) => (
                      <div className="fila-simple" key={a.texto}>
                        <span>{a.texto}</span>
                        <span className="dato">
                          {a.porcentaje} % · {a.backlinks}
                        </span>
                      </div>
                    ))}
                  </details>
                )}

                {datos.enlaces.paises.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">De qué países vienen</span>
                      <span className="dato">
                        {datos.enlaces.paises.length}
                      </span>
                    </summary>
                    {datos.enlaces.paises.map((p) => (
                      <div className="fila-simple" key={p.pais}>
                        <span>{p.pais}</span>
                        <span className="dato">{p.dominios} dominios</span>
                      </div>
                    ))}
                  </details>
                )}
              </>
            )}
          </section>

          <section className="caja-seo">
            <header>
              <h3>Enlazado interno</h3>
              {datos.enlazado && (
                <span className="pill">{datos.enlazado.dia}</span>
              )}
              <span className="pista">
                Quién enlaza a quién dentro de la propia web.
              </span>
            </header>

            {!datos.enlazado ? (
              <p className="pista">
                Todavía sin mapear. Se recorren las páginas del sitemap una vez
                al mes: es lo único que dice qué páginas no enlaza nadie y desde
                dónde habría que enlazarlas.
              </p>
            ) : (
              <>
                <div className="resumen-auditoria">
                  <span>
                    <strong>{datos.enlazado.rastreadas}</strong> páginas leídas
                  </span>
                  {datos.enlazado.huerfanas.length > 0 && (
                    <span className="peor">
                      {datos.enlazado.huerfanas.length} sin enlaces entrantes
                    </span>
                  )}
                  {datos.enlazado.callejones.length > 0 && (
                    <span className="sin">
                      {datos.enlazado.callejones.length} sin enlaces salientes
                    </span>
                  )}
                </div>

                {/*
                 * Lo primero: las páginas que PAGAN.
                 *
                 * El blog existe para alimentarlas. Si no las enlaza, son
                 * cuarenta artículos trabajando para nadie — y eso no lo dice
                 * ninguna herramienta.
                 */}
                {datos.enlazado.servicios.length > 0 && (
                  <>
                    <p className="pista recomienda">
                      Apoyo que reciben las páginas de servicio, las que traen
                      clientes:
                    </p>
                    {datos.enlazado.servicios.map((s) => (
                      <div className="fila-simple" key={s.url}>
                        <span className={s.flojo ? "peor" : ""}>{s.url}</span>
                        <span className="dato">
                          {s.medida ? (
                            <strong className={s.flojo ? "peor" : "mejor"}>
                              {s.entrantes}
                            </strong>
                          ) : (
                            <span
                              className="sin"
                              title="No está en el sitemap, así que no se recorrió"
                            >
                              sin medir
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {datos.enlazado.huerfanas.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">
                        Páginas a las que no enlaza nadie
                      </span>
                      <span className="pill">días</span>
                      <span className="dato">
                        {datos.enlazado.huerfanas.length}
                      </span>
                    </summary>
                    <p className="como">
                      Google reparte la fuerza del dominio por los enlaces
                      internos: una página a la que no apunta nadie desde dentro
                      compite como si estuviera sola. Se arregla enlazándolas
                      desde páginas que traten de lo mismo — y si son artículos
                      de blog, que enlacen ellos a la página de servicio que les
                      toca.
                    </p>
                    <div className="ejemplos">
                      {datos.enlazado.huerfanas.slice(0, 30).map((u) => (
                        <a
                          key={u}
                          href={`https://${u}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {u}
                        </a>
                      ))}
                    </div>
                  </details>
                )}

                {datos.enlazado.masEnlazadas.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">
                        Dónde está yendo la fuerza hoy
                      </span>
                      <span className="dato">
                        {datos.enlazado.masEnlazadas.length}
                      </span>
                    </summary>
                    {datos.enlazado.masEnlazadas.map((p) => (
                      <div className="fila-simple" key={p.url}>
                        <span>{p.url}</span>
                        <span className="dato">{p.entrantes}</span>
                      </div>
                    ))}
                  </details>
                )}
              </>
            )}
          </section>

          <section className="caja-seo">
            <header>
              <h3>Sitemap</h3>
              {datos.sitemap && (
                <span className="pill">{datos.sitemap.dia}</span>
              )}
              <span className="pista">Lo que la web dice tener publicado.</span>
            </header>

            {!datos.sitemap ? (
              <p className="pista">
                Todavía sin leer, o no se ha encontrado. Se busca solo en el
                robots.txt y en las direcciones habituales; si está en un sitio
                raro, se apunta en Configuración.
              </p>
            ) : (
              <>
                <div className="resumen-auditoria">
                  <span>
                    <strong>
                      {datos.sitemap.cuantas.toLocaleString("es-ES")}
                    </strong>{" "}
                    páginas
                  </span>
                  {datos.sitemap.nuevas.length > 0 && (
                    <span className="mejor">
                      ▲ {datos.sitemap.nuevas.length} nuevas
                    </span>
                  )}
                  {datos.sitemap.desaparecidas.length > 0 && (
                    <span className="peor">
                      ▼ {datos.sitemap.desaparecidas.length} han desaparecido
                    </span>
                  )}
                </div>

                <a
                  className="donde"
                  href={datos.sitemap.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {datos.sitemap.url}
                </a>

                {/* Qué se ha publicado. Es lo que contesta «¿se está haciendo
                  contenido?» sin preguntárselo a nadie. */}
                {datos.sitemap.nuevas.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">
                        Páginas nuevas desde la lectura anterior
                      </span>
                      <span className="dato">
                        {datos.sitemap.nuevas.length}
                      </span>
                    </summary>
                    <div className="ejemplos">
                      {datos.sitemap.nuevas.slice(0, 30).map((u) => (
                        <a
                          key={u}
                          href={`https://${u}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {u}
                        </a>
                      ))}
                    </div>
                  </details>
                )}

                {/*
                 * Y lo que ha desaparecido, que se olvida y es más caro.
                 *
                 * Una página que estaba, ya no está y posicionaba explica sola
                 * una caída de clics — sin tener que buscar por qué.
                 */}
                {datos.sitemap.desaparecidas.length > 0 && (
                  <details className="problema">
                    <summary>
                      <span className="nombre">
                        Páginas que han desaparecido
                      </span>
                      <span className="pill peor">mirar</span>
                      <span className="dato">
                        {datos.sitemap.desaparecidas.length}
                      </span>
                    </summary>
                    <p className="como">
                      Alguien las borró o las despublicó. Si alguna posicionaba,
                      esto explica una caída de clics sin tener que buscar más.
                    </p>
                    <div className="ejemplos">
                      {datos.sitemap.desaparecidas.slice(0, 30).map((u) => (
                        <a
                          key={u}
                          href={`https://${u}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {u}
                        </a>
                      ))}
                    </div>
                  </details>
                )}

                {datos.sitemap.nuevas.length === 0 &&
                  datos.sitemap.desaparecidas.length === 0 && (
                    <p className="pista">
                      Sin cambios desde la lectura anterior: no se ha publicado
                      ni retirado nada.
                    </p>
                  )}
              </>
            )}
          </section>

          <section className="caja-seo">
            <header>
              <h3>Velocidad</h3>
              {datos.velocidad && (
                <span className="pill">{datos.velocidad.dia}</span>
              )}
              <span className="pista">Medido por Google, en móvil.</span>
            </header>

            {!datos.velocidad ? (
              /*
               * El porqué ya lo dice la cifra de arriba, con su estado.
               *
               * Aquí ponía «todavía sin medir», que valía igual para «le toca
               * el jueves» que para «no hay clave de PageSpeed y no le va a
               * tocar nunca». Una frase amable tapando una tubería rota.
               */
              <p className="pista">
                El detalle de esta caja sale cuando haya una medición. Arriba
                pone por qué no la hay.
              </p>
            ) : (
              <>
                <div className="salud">
                  {datos.velocidad.nota !== null && (
                    <Anillo porcentaje={datos.velocidad.nota} nota />
                  )}
                  <div className="gravedades">
                    <Vital
                      que="Pintar lo grande"
                      ms={datos.velocidad.lcp}
                      limite={2500}
                    />
                    <Vital
                      que="Contenido que se mueve"
                      ms={datos.velocidad.cls}
                      limite={0.1}
                      decimal
                    />
                    <Vital
                      que="Se queda sin responder"
                      ms={datos.velocidad.tbt}
                      limite={200}
                    />
                  </div>
                </div>

                <div className="resumen-auditoria">
                  {datos.velocidad.pesoTotalKb !== null && (
                    <span>
                      <strong>
                        {datos.velocidad.pesoTotalKb.toLocaleString("es-ES")}
                      </strong>{" "}
                      KB de descarga
                    </span>
                  )}
                  {datos.velocidad.antes?.nota != null &&
                    datos.velocidad.nota !== null && (
                      <span
                        className={
                          datos.velocidad.nota >= datos.velocidad.antes.nota
                            ? "mejor"
                            : "peor"
                        }
                      >
                        {datos.velocidad.nota >= datos.velocidad.antes.nota
                          ? "▲"
                          : "▼"}{" "}
                        {Math.abs(
                          datos.velocidad.nota - datos.velocidad.antes.nota,
                        )}{" "}
                        desde {datos.velocidad.antes.dia}
                      </span>
                    )}
                </div>

                {/*
                 * De dónde sale el número, dicho sin adornarlo.
                 *
                 * Presentar una simulación como lo que vive la gente manda a
                 * optimizar lo que a lo mejor no molesta a nadie.
                 */}
                <p className="pista">
                  {datos.velocidad.deUsuariosReales
                    ? "Datos de usuarios REALES de Chrome: es exactamente lo que Google usa para posicionar."
                    : "Simulación en un móvil de gama media: esta web no tiene visitas suficientes para que Chrome dé el dato real."}
                </p>

                {datos.velocidad.recomendaciones.length > 0 && (
                  <>
                    <p className="pista recomienda">
                      Lo que recomienda Google, por lo que se ahorra:
                    </p>
                    {datos.velocidad.recomendaciones.map((r) => (
                      <details className="problema" key={r.id}>
                        <summary>
                          <span className="nombre">{r.que}</span>
                          <span className="pill">{r.plazo}</span>
                          <span className="dato">
                            {r.ahorroMs >= 1000
                              ? `${(r.ahorroMs / 1000).toFixed(1).replace(".", ",")} s`
                              : `${r.ahorroMs} ms`}
                          </span>
                        </summary>
                        <p className="como">{r.como}</p>
                      </details>
                    ))}
                  </>
                )}
              </>
            )}
          </section>

          <section className="caja-seo">
            <header>
              <h3>Auditoría técnica</h3>
              {datos.auditoria.mes && (
                <span className="pill">{datos.auditoria.mes}</span>
              )}
            </header>
            {datos.auditoria.problemas === 0 ? (
              <p className="pista">
                Sin auditoría importada. Llega sola al buzón con el informe
                mensual de SE Ranking.
              </p>
            ) : (
              <>
                {datos.auditoria.mejorQueElPorcentaje !== null && (
                  <div className="salud">
                    <Anillo porcentaje={datos.auditoria.mejorQueElPorcentaje} />
                    <div className="gravedades">
                      {datos.auditoria.errores !== null && (
                        <div className="fila-simple">
                          <span className="peor">Errores</span>
                          <span className="dato">
                            {datos.auditoria.errores}
                          </span>
                        </div>
                      )}
                      {datos.auditoria.advertencias !== null && (
                        <div className="fila-simple">
                          <span>Advertencias</span>
                          <span className="dato">
                            {datos.auditoria.advertencias}
                          </span>
                        </div>
                      )}
                      {datos.auditoria.avisos !== null && (
                        <div className="fila-simple">
                          <span className="sin">Avisos</span>
                          <span className="dato">{datos.auditoria.avisos}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="resumen-auditoria">
                  <span>
                    <strong>{datos.auditoria.problemas}</strong> problemas
                  </span>
                  <span>
                    <strong>{datos.auditoria.paginasAfectadas}</strong> páginas
                  </span>
                  {datos.auditoria.arreglados > 0 && (
                    <span className="mejor">
                      ▲ {datos.auditoria.arreglados} arreglados
                    </span>
                  )}
                  {datos.auditoria.nuevos > 0 && (
                    <span className="peor">
                      ▼ {datos.auditoria.nuevos} nuevos
                    </span>
                  )}
                </div>
                {/**
                 * Cada problema se abre y dice QUE paginas y COMO se arregla.
                 *
                 * Cerrado se lee la lista de un vistazo; abierto se puede
                 * encargar. Antes solo ponia el nombre y el numero, y con eso no
                 * se puede mandar a nadie a hacer nada.
                 */}
                {datos.auditoria.principales.map((p) => (
                  <details className="problema" key={p.problema}>
                    <summary>
                      {/*
                       * La gravedad, delante del nombre y con su palabra.
                       *
                       * Sólo con color no se lee: un punto rojo y uno ámbar a
                       * 11 píxeles son dos puntos grises para quien no
                       * distingue esos dos, y no hay nada más que lo diga.
                       *
                       * Y cuando no consta se dice que no consta, en vez de no
                       * pintar nada: un hueco se lee como <<sin problema>>.
                       */}
                      <span
                        className={`pill gravedad ${p.gravedad ?? "sinGravedad"}`}
                        title={
                          p.gravedad
                            ? "Gravedad que le pone SE Ranking"
                            : "Este problema entró antes de que se leyera la gravedad del informe. No quiere decir que sea leve."
                        }
                      >
                        {EMOJI_DE_GRAVEDAD[p.gravedad ?? ""] ?? "⚪"}{" "}
                        {p.gravedad ?? "sin gravedad"}
                      </span>
                      <span className="nombre">{p.problema}</span>
                      {p.nuevos > 0 && (
                        <span className="pill peor">+{p.nuevos}</span>
                      )}
                      {p.solucionados > 0 && (
                        <span className="pill ok">−{p.solucionados}</span>
                      )}
                      {p.plazo && <span className="pill">{p.plazo}</span>}
                      <span className="dato">{p.paginas}</span>
                    </summary>

                    {p.como ? (
                      <p className="como">{p.como}</p>
                    ) : (
                      <p className="pista">
                        Sin remedio escrito para éste todavía. Pregúntaselo a
                        BILBOBOT.
                      </p>
                    )}
                    {p.cuidado && (
                      <p className="cuidado">Cuidado: {p.cuidado}</p>
                    )}

                    {/*
                     * Un problema no es una tarea hasta que alguien lo decide.
                     *
                     * Hay 29 y no se atacan los 29. Al pasarlo se lleva el
                     * número de páginas como punto de partida: si el mes que
                     * viene son menos, se arregló.
                     */}
                    <div className="mandos">
                      {yaEsTarea.has(p.problema) ? (
                        <span className="pista">Ya está en Tareas.</span>
                      ) : (
                        <button
                          type="button"
                          disabled={ocupado !== null}
                          onClick={() =>
                            void mandar("/acciones", "POST", {
                              queHacer: `Arreglar: ${p.problema}`,
                              porQue: p.como
                                ? `${p.paginas} páginas afectadas. ${p.como}`
                                : `${p.paginas} páginas afectadas.`,
                              tipo: "tecnico",
                              paginas: p.paginas,
                            }).then(setFallo)
                          }
                        >
                          Pasar a Tareas
                        </button>
                      )}
                    </div>

                    {p.ejemplos.length > 0 && (
                      <div className="ejemplos">
                        <span className="pista">
                          {p.paginas > p.ejemplos.length
                            ? `${p.ejemplos.length} de las ${p.paginas} páginas:`
                            : "Las páginas:"}
                        </span>
                        {p.ejemplos.map((u) => (
                          <a key={u} href={u} target="_blank" rel="noreferrer">
                            {u}
                          </a>
                        ))}
                      </div>
                    )}
                  </details>
                ))}
              </>
            )}

            {datos.autoridad && (
              <div className="autoridad">
                <span>
                  <strong>{datos.autoridad.dominiosDeReferencia ?? "—"}</strong>{" "}
                  dominios
                </span>
                <span>
                  Trust <strong>{datos.autoridad.domainTrust ?? "—"}</strong>
                </span>
                {(datos.autoridad.noIndexables ?? 0) > 0 && (
                  <span className="peor">
                    {datos.autoridad.noIndexables} no indexables
                  </span>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
